import type { ComponentType } from "react";

import { MandatoryParamsEmptyState } from "app/components/React/HOCs/accessWithoutMandatoryParamsHOC";
import {
    useSelectedIndustry
} from "app/pages/digital-marketing/ad-intelligence/shared/components/AssetPicker/useSelectedIndustry";

type SelectedIndustry = NonNullable<ReturnType<typeof useSelectedIndustry>["industry"]>;

/**
 * Wraps a domain report and its industry counterpart into the single component a report state
 * mounts, so the `industry` URL param picks the report instead of being silently ignored.
 *
 * Call once per report at module scope: each call returns a new component type, and recreating it
 * during render would remount the report on every transition.
 */
export const createIndustryAwareRoute = <P extends object>(
    DomainReport: ComponentType<P>,
    IndustryReport: ComponentType<{ category: SelectedIndustry }>,
) => {
    const IndustryAwareRoute = (props: P) => {
        const { industry, industryParam } = useSelectedIndustry();

        if (industry) {
            return <IndustryReport category={industry} />;
        }
        // Industry param present but unresolvable: show the empty state instead of falling
        // through to the domain report, which crashes on the absent `key`.
        if (industryParam) {
            return <MandatoryParamsEmptyState missingItemType="website" />;
        }
        return <DomainReport {...props} />;
    };

    return IndustryAwareRoute;
};
